import { useQuery } from '@apollo/client';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import equal from 'deep-equal';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { BooleanParam, createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import DateInput from '@components/fields/DateInput';
import EmployerSelect from '@components/fields/EmployerSelect';
import Select from '@components/fields/Select';
import styles from '@components/forms/AdminJourneyFilters.module.css';
import Form from '@components/forms/Form';
import HandleQuery from '@components/HandleQuery';
import Icon from '@components/text/Icon';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import { NeedsInterpretationEnum } from '@typing/Enums';
import {
  ConsultDurationEnum,
  ConsultReportStatusEnum,
  ConsultTimePeriodEnum,
  ExpertGlobalRoleEnum,
  GetExpertsBasicDocument,
  GetExpertsBasicQuery,
  GetExpertsBasicQueryVariables
} from '@typing/Generated';
import { onChangeWithPaid } from '@utils/formUtils';

type FormData = {
  consultReportStatus?: ConsultReportStatusEnum | null;
  duration?: ConsultDurationEnum | null;
  employerId?: string | null;
  endTime?: string | null;
  expertId?: string | null;
  needsInterpretation?: NeedsInterpretationEnum | null;
  selfPaid?: boolean | null;
  startTime?: string | null;
  timePeriod?: ConsultTimePeriodEnum | null;
};

type Props = {
  filters: FormData;
  onChange: (filterValues: FormData) => void;
};

const ExpertConsultsFilters = ({ filters, onChange }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const consultReportStatusParam = createEnumParam<ConsultReportStatusEnum>(Object.values(ConsultReportStatusEnum));
  const timePeriodParam = createEnumParam<ConsultTimePeriodEnum>(Object.values(ConsultTimePeriodEnum));
  const NeedsInterpretationParam = createEnumParam<NeedsInterpretationEnum>(Object.values(NeedsInterpretationEnum));

  const [query, setQuery] = useQueryParams({
    consultReportStatus: withDefault(
      { ...consultReportStatusParam, urlName: 'consultCRStatus' },
      filters.consultReportStatus
    ),
    employerId: withDefault({ ...StringParam, urlName: 'consultEmployerID' }, filters.employerId),
    employerName: { ...StringParam, urlName: 'consultEmployerName' },
    endTime: withDefault({ ...StringParam, urlName: 'consultEndTime' }, filters.endTime),
    expertId: withDefault({ ...StringParam, urlName: 'consultExpertId' }, filters.expertId),
    needsInterpretation: withDefault(
      { ...NeedsInterpretationParam, urlName: 'consultNeedsInterpretation' },
      filters.needsInterpretation
    ),
    selfPaid: withDefault({ ...BooleanParam, urlName: 'consultSelfPaid' }, filters.selfPaid),
    startTime: withDefault({ ...StringParam, urlName: 'consultStartTime' }, filters.startTime),
    timePeriod: withDefault({ ...timePeriodParam, urlName: 'consultTimePeriod' }, filters.timePeriod)
  });

  const defaultValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange'
  });

  const getExpertsQuery = useQuery<GetExpertsBasicQuery, GetExpertsBasicQueryVariables>(GetExpertsBasicDocument);

  useEffect(() => {
    if (!getExpertsQuery.loading && getExpertsQuery.data?.experts.pageInfo.hasNextPage) {
      getExpertsQuery.fetchMore({ variables: { cursor: getExpertsQuery.data?.experts.pageInfo.endCursor ?? '' } });
    }
  }, [getExpertsQuery]);

  const experts = useNodesFromConnection(getExpertsQuery.data?.experts).filter(expert => expert.id !== myExpert?.id);

  const consultReportStatus = form.watch('consultReportStatus');
  const employerId = form.watch('employerId');
  const expertId = form.watch('expertId');
  const needsInterpretation = form.watch('needsInterpretation');
  const timePeriod = form.watch('timePeriod');
  const startTime = form.watch('startTime');
  const endTime = form.watch('endTime');
  const selfPaid = form.watch('selfPaid');

  useEffect(() => {
    if (myExpert) {
      const values = {
        consultReportStatus,
        employerId,
        endTime,
        expertId,
        needsInterpretation,
        selfPaid,
        startTime,
        timePeriod
      };
      if (!equal(values, filters)) {
        setQuery(values);
        onChangeWithPaid(onChange, values);
      }
    }
  }, [
    consultReportStatus,
    employerId,
    endTime,
    expertId,
    filters,
    myExpert,
    needsInterpretation,
    onChange,
    selfPaid,
    setQuery,
    startTime,
    timePeriod
  ]);

  const consultReportStatusOptions = Object.values(ConsultReportStatusEnum).map(option => ({
    label: intl.formatMessage({ id: `models.expert.consultReportStatus.${option}` }),
    value: option
  }));

  const consultTimePeriodOptions = useMemo(() => {
    const options = [ConsultTimePeriodEnum.all, ConsultTimePeriodEnum.upcoming, ConsultTimePeriodEnum.past];

    return options.map(option => ({
      label: intl.formatMessage({ id: `models.expert.consultTimePeriod.${option}` }),
      value: option
    }));
  }, [intl]);
  const needsInterpretationOptions = useMemo(() => {
    const options = [NeedsInterpretationEnum.ALL, NeedsInterpretationEnum.NEEDS_INTERPRETATION];

    return options.map(option => ({
      label: intl.formatMessage({ id: `models.expert.needsInterpretation.${option}` }),
      value: option
    }));
  }, [intl]);

  const expertOptions = useMemo(() => {
    const options = experts.map(expert => ({
      label: expert.fullName,
      value: expert.id
    }));

    options.unshift({ label: intl.formatMessage({ id: 'dictionary.all' }), value: '' });
    if (myExpert) {
      options.unshift({ label: intl.formatMessage({ id: 'dictionary.me' }), value: myExpert.id });
    }
    return options;
  }, [experts, intl, myExpert]);

  return (
    <HandleQuery query={getExpertsQuery}>
      {_expertData => (
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        <Form className={styles.form} hookForm={form} onSubmit={() => {}}>
          <IonGrid>
            <IonRow className="ion-align-items-center">
              <IonCol>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.timePeriod}
                  label={intl.formatMessage({ id: 'forms.expertConsultsFilters.timePeriodLabel' })}
                  name="timePeriod"
                  options={consultTimePeriodOptions}
                />
              </IonCol>
              <IonCol className={myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager ? undefined : 'ion-hide'}>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.expertId}
                  label={intl.formatMessage({ id: 'forms.expertConsultsFilters.expertLabel' })}
                  name="expertId"
                  options={expertOptions}
                />
              </IonCol>
              <IonCol>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.consultReportStatus}
                  label={intl.formatMessage({ id: 'forms.expertConsultsFilters.consultReportStatusLabel' })}
                  name="consultReportStatus"
                  options={consultReportStatusOptions}
                />
              </IonCol>
              <IonCol>
                <div className={styles.item}>
                  <DateInput<FormData>
                    control={form.control}
                    error={form.formState.errors.startTime}
                    label={intl.formatMessage({ id: 'forms.expertConsultsFilters.startTimeLabel' })}
                    name="startTime"
                  />
                  <div
                    aria-label={intl.formatMessage({ id: 'accessibility.ariaLabels.delete' })}
                    className={styles.clear}
                    onClick={() => form.setValue('startTime', '')}
                  >
                    <Icon icon="times" />
                  </div>
                </div>
              </IonCol>
              <IonCol>
                <div className={styles.item}>
                  <DateInput<FormData>
                    control={form.control}
                    error={form.formState.errors.endTime}
                    label={intl.formatMessage({ id: 'forms.expertConsultsFilters.endTimeLabel' })}
                    name="endTime"
                  />
                  <div
                    aria-label={intl.formatMessage({ id: 'accessibility.ariaLabels.delete' })}
                    className={styles.clear}
                    onClick={() => form.setValue('endTime', '')}
                  >
                    <Icon icon="times" />
                  </div>
                </div>
              </IonCol>
              <IonCol>
                <EmployerSelect<FormData>
                  allowAny
                  control={form.control}
                  error={form.formState.errors.employerId}
                  initialOption={{ label: query.employerName ?? '', value: query.employerId ?? '' }}
                  label={intl.formatMessage({ id: 'forms.expertConsultsFilters.employerLabel' })}
                  name="employerId"
                  onSelection={(option: { label: string }) => {
                    setQuery({ ...query, ...{ employerName: option.label } });
                  }}
                />
              </IonCol>
              <IonCol>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.needsInterpretation}
                  label={intl.formatMessage({ id: 'forms.expertConsultsFilters.needsInterpretationLabel' })}
                  name="needsInterpretation"
                  options={needsInterpretationOptions}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        </Form>
      )}
    </HandleQuery>
  );
};

export default ExpertConsultsFilters;
